import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, ArrowUp, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks: { href: string; label: string; external?: boolean }[] = [
  { href: "/", label: "Ana Sayfa" },
  { href: "/facebook-reklam-hesabi-kiralama", label: "Hesap Kiralama" },
  { href: "/hizmetler/reklam-yonetimi", label: "Reklam Yönetimi" },
  { href: "/ozellikler", label: "Özellikler" },
  { href: "/blog", label: "Blog" },
  { href: "/fiyatlar", label: "Fiyatlar" },
];

/** Header/menü ana çağrısı: panel kurulumu tamamlanana kadar kayıt yerine WhatsApp teklif */
const TEKLIF_LINK = "https://wa.me/905320522545?text=Merhaba%2C%20reklam%20hesab%C4%B1%20kiralama%20ve%20reklam%20y%C3%B6netimi%20i%C3%A7in%20teklif%20almak%20istiyorum.";

function Navbar() {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Mobil menü açıkken arka plan kaydırmasını kilitle, Esc ile kapat
  useEffect(() => {
    if (!mobileOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-sm border-b-3 border-[#0A1A3F] shadow-[0_4px_0_0_#0A1A3F]"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 h-16 sm:h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="/adsmanager-logo.png"
              alt="AdsManager"
              width={180}
              height={40}
              className="h-6 sm:h-8 lg:h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5 xl:gap-1 flex-nowrap">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap px-2.5 xl:px-3.5 py-2 font-bold text-xs xl:text-sm uppercase tracking-wide xl:tracking-wider transition-all duration-200 text-[#0A1A3F] hover:bg-[#6B8AFE]/10 border-2 border-transparent"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`whitespace-nowrap px-2.5 xl:px-3.5 py-2 font-bold text-xs xl:text-sm uppercase tracking-wide xl:tracking-wider transition-all duration-200 ${
                    location === link.href
                      ? "bg-[#6B8AFE] text-white border-2 border-[#0A1A3F] shadow-[2px_2px_0_0_#0A1A3F]"
                      : "text-[#0A1A3F] hover:bg-[#6B8AFE]/10 border-2 border-transparent"
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <a
              href={TEKLIF_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 whitespace-nowrap flex-shrink-0 border-3 border-[#0A1A3F] bg-[#6B8AFE] text-white font-bold text-xs xl:text-sm uppercase tracking-wider px-4 xl:px-5 py-2.5 shadow-[3px_3px_0_0_#0A1A3F] hover:shadow-[1px_1px_0_0_#0A1A3F] hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[3px] active:translate-y-[3px] transition-all flex items-center gap-2"
            >
              <MessageCircle size={14} />
              Teklif Alın
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
            aria-expanded={mobileOpen}
            aria-controls="mobil-menu"
            className="lg:hidden w-11 h-11 flex items-center justify-center border-2 border-[#0A1A3F] bg-white text-[#0A1A3F] shadow-[2px_2px_0_0_#0A1A3F] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            id="mobil-menu"
            className="lg:hidden overflow-hidden bg-white border-b-3 border-[#0A1A3F] shadow-[0_4px_0_0_#0A1A3F]"
          >
            <div className="px-4 py-3 flex flex-col gap-1 max-h-[calc(100vh-5.75rem)] supports-[height:100dvh]:max-h-[calc(100dvh-5.75rem)] overflow-y-auto overscroll-contain">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 font-bold text-sm uppercase tracking-wider transition-all text-[#0A1A3F] border-2 border-transparent hover:border-[#0A1A3F] active:bg-[#6B8AFE]/10"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 font-bold text-sm uppercase tracking-wider transition-all ${
                      location === link.href
                        ? "bg-[#6B8AFE] text-white border-2 border-[#0A1A3F] shadow-[2px_2px_0_0_#0A1A3F]"
                        : "text-[#0A1A3F] border-2 border-transparent hover:border-[#0A1A3F] active:bg-[#6B8AFE]/10"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <a
                href={TEKLIF_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="border-3 border-[#0A1A3F] bg-[#6B8AFE] text-white text-center font-bold uppercase tracking-wider px-4 py-3 shadow-[3px_3px_0_0_#0A1A3F] flex items-center justify-center gap-2 mt-2 mb-1"
              >
                <MessageCircle size={16} />
                WhatsApp'tan Teklif Alın
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A1A3F] text-white border-t-3 border-[#0A1A3F]">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-8 sm:gap-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <img
              src="/adsmanager-logo.png"
              alt="AdsManager"
              width={180}
              height={40}
              loading="lazy"
              className="h-8 sm:h-10 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/70 text-sm leading-relaxed mt-4">
              Facebook, Instagram, Google Ads ve TikTok için yüksek limitli ajans reklam hesabı kiralama,
              reklam yönetimi ve danışmanlık.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-base sm:text-lg mb-4 text-[#6B8AFE]">Hızlı Bağlantılar</h3>
            <ul className="space-y-0.5">
              {navLinks.map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Hizmetler */}
          <div>
            <h3 className="font-bold text-base sm:text-lg mb-4 text-[#6B8AFE]">Hizmetler</h3>
            <ul className="space-y-0.5">
              <li>
                <Link href="/facebook-reklam-hesabi-kiralama" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  Facebook Reklam Hesabı Kiralama
                </Link>
              </li>
              <li>
                <Link href="/hizmetler/reklam-yonetimi" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  Reklam Yönetimi Hizmeti
                </Link>
              </li>
              <li>
                <Link href="/hizmetler/e-ticaret-reklam-yonetimi" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  E-Ticaret Reklam Yönetimi
                </Link>
              </li>
              <li>
                <Link href="/hizmetler/seo-geo-hizmeti" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  SEO & GEO Hizmeti
                </Link>
              </li>
            </ul>
          </div>

          {/* Platform */}
          <div>
            <h3 className="font-bold text-base sm:text-lg mb-4 text-[#6B8AFE]">Hesap ve Fiyatlar</h3>
            <ul className="space-y-0.5">
              <li>
                <a href="https://admin.adsmanager.com.tr" target="_blank" rel="noopener noreferrer" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  Panele Giriş
                </a>
              </li>
              <li>
                <Link href="/ozellikler" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  Tüm Özellikler
                </Link>
              </li>
              <li>
                <Link href="/fiyatlar" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  Kiralama ve Danışmanlık Fiyatları
                </Link>
              </li>
              <li>
                <Link href="/blog" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm font-medium">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="font-bold text-base sm:text-lg mb-4 text-[#6B8AFE]">İletişim</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-[#6B8AFE] flex-shrink-0" />
                <a href="tel:+905320522545" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm">
                  0532 052 25 45
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-[#6B8AFE] flex-shrink-0" />
                <a href="mailto:admin@dijipo.com" className="inline-block py-1.5 text-white/70 hover:text-[#6B8AFE] transition-colors text-sm break-all">
                  admin@dijipo.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/50 text-xs sm:text-sm">
            © {new Date().getFullYear()} AdsManager. Tüm hakları saklıdır.
          </p>
          <p className="text-white/50 text-xs sm:text-sm">
            Bir{" "}
            <a
              href="https://dijipo.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#6B8AFE] hover:underline font-medium inline-block py-1"
            >
              Dijipo
            </a>{" "}
            ürünüdür.
          </p>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  return (
    <a
      href="https://wa.me/905320522545?text=Merhaba%2C%20AdsManager%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-[#25D366] border-2 sm:border-3 border-[#0A1A3F] shadow-[2px_2px_0_0_#0A1A3F] sm:shadow-[3px_3px_0_0_#0A1A3F] flex items-center justify-center hover:shadow-[1px_1px_0_0_#0A1A3F] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
      aria-label="WhatsApp ile iletişime geçin"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6 sm:w-7 sm:h-7 fill-white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
}

function ScrollToTopButton() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!show) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-4 right-[4.5rem] sm:bottom-6 sm:right-24 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-[#6B8AFE] text-white border-2 sm:border-3 border-[#0A1A3F] shadow-[2px_2px_0_0_#0A1A3F] sm:shadow-[3px_3px_0_0_#0A1A3F] flex items-center justify-center hover:shadow-[1px_1px_0_0_#0A1A3F] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
      aria-label="Yukarı çık"
    >
      <ArrowUp size={20} />
    </button>
  );
}

// Page transition animation wrapper
function PageTransition({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Page loading bar
function PageLoadingBar() {
  const [location] = useLocation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [location]);

  return (
    <AnimatePresence>
      {loading && (
        <motion.div
          className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-[#6B8AFE]/20"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="h-full bg-[#6B8AFE]"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col">
      <PageLoadingBar />
      <Navbar />
      <main className="flex-1 pt-16 sm:pt-20">
        <PageTransition>{children}</PageTransition>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
}
